import { useEffect } from 'react'
import { store } from 'app/state/store'
import { BackendEvent } from '../api/backendEvents'
import { EventStreamEventType, isEventSourceMessage, connectEventStream, EventSourceMessage } from '../api/eventStream'
import { backendEvent } from '../state/commonSlice'
import settings from 'settings'
import { getWatchToken } from '../api/watch'
const { notifierUrl } = settings

/**
 * EventSource emits raw strings, containing JSON-encoded payloads.
 * Parse them and pass to registered event handlers.
 */
function backendEventMessageHandler(message: EventSourceMessage | EventStreamEventType) {
  if (!isEventSourceMessage(message)) {
    return
  }

  let event: BackendEvent
  try {
    event = JSON.parse(message.data)
  } catch (error) {
    console.error(error)
    return
  }

  handlers.forEach((handler) => {
    try {
      handler(event)
    } catch (error) {
      console.error(error)
    }
  })
}

type BackendEventHandler = (event: BackendEvent) => void
let handlers: BackendEventHandler[] = [dispatchToStore]

function dispatchToStore(event: BackendEvent) {
  store.dispatch(backendEvent(event))
}

let refCount = 0
let eventStreamUnsubscribe: (() => void) | null = null

/**
 * Use this in one of top components to register into server notifications.
 *
 * Notifications will be posted as redux actions, so no
 * need
 */
export function useBackendEvents(callback?: (event: BackendEvent) => void) {
  useEffect(() => {
    if (!eventStreamUnsubscribe) {
      eventStreamUnsubscribe = connectEventStream(
        `${notifierUrl}/watch`,
        getWatchToken,
        backendEventMessageHandler
      ).unsubscribe
    }
    ++refCount
    if (callback) {
      handlers.push(callback)
    }
    return () => {
      if (callback) {
        handlers = handlers.filter((c) => c !== callback)
      }

      // cleanup must be async, so we don't close stream too quickly when our
      // user reruns effect with different callback
      setTimeout(() => {
        if (--refCount === 0 && eventStreamUnsubscribe) {
          eventStreamUnsubscribe()
          eventStreamUnsubscribe = null
        }
      }, 0)
    }
  }, [callback])
}

export function subscribeBackendEvents(onEvent: (event: BackendEvent) => void) {
  const handler = (event: BackendEvent) => {
    onEvent(event)
  }
  const unsubscribe = () => {
    handlers = handlers.filter((c) => c !== handler)
  }
  handlers.push(handler)

  return unsubscribe
}
